import { Button, Checkbox, Input, Modal, Tooltip } from "@legal-500/legal500-design-system";
import { rolesData } from "@legal-500/permissions";
import { useEffect, useRef, useState } from "react";
import {
  userManagement,
  type CreateUserPayload,
  type CreateUserResult,
  type Organisation,
  type UserContext,
} from "../../../api/userManagement.service";
import styles from "../UserManagement.module.css";
import createUserStyles from "./CreateUserModal.module.css";

const STAFF_PLATFORM_ROLES = rolesData
  .filter(role => role.context === "staff")
  .map(role => ({ name: role.name, description: role.description ?? "" }))
  .sort((a, b) => a.name.localeCompare(b.name));

const ORG_SEARCH_DEBOUNCE_MS = 300;
const ORG_SEARCH_LIMIT = 20;

export type CreateUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: CreateUserResult) => void;
  onError?: (error: Error) => void;
  context: UserContext;
};

type ApiErrorWithStatus = Error & { status?: number; code?: string };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isApiError(candidate: unknown): candidate is ApiErrorWithStatus {
  return candidate instanceof Error;
}

function getFriendlyErrorMessage(error: unknown): string {
  if (!isApiError(error)) return "Failed to create user";
  if (error.status === 409) {
    return "A user with that email already exists.";
  }
  return error.message || "Failed to create user";
}

export function CreateUserModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  context,
}: CreateUserModalProps) {
  const isAccountContext = context === "account";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    organisation?: string;
  }>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgResults, setOrgResults] = useState<Organisation[]>([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [orgSearchError, setOrgSearchError] = useState<string | null>(null);
  const [isOrgListOpen, setIsOrgListOpen] = useState(false);
  const orgDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orgRequestIdRef = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setEmail("");
      setSelectedRoles([]);
      setFieldErrors({});
      setServerError(null);
      setIsSubmitting(false);
      setSelectedOrganisation(null);
      setOrgSearch("");
      setOrgResults([]);
      setOrgSearchLoading(false);
      setOrgSearchError(null);
      setIsOrgListOpen(false);
      if (orgDebounceRef.current) {
        clearTimeout(orgDebounceRef.current);
        orgDebounceRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (orgDebounceRef.current) clearTimeout(orgDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isAccountContext) return;
    if (selectedOrganisation && orgSearch === selectedOrganisation.name) return;

    if (orgDebounceRef.current) clearTimeout(orgDebounceRef.current);

    const term = orgSearch.trim();
    if (term.length === 0) {
      setOrgResults([]);
      setOrgSearchLoading(false);
      setOrgSearchError(null);
      return;
    }

    setOrgSearchLoading(true);
    setOrgSearchError(null);
    orgDebounceRef.current = setTimeout(async () => {
      const requestId = ++orgRequestIdRef.current;
      try {
        const response = await userManagement.getOrganisations({
          search: term,
          limit: ORG_SEARCH_LIMIT,
        });
        if (requestId !== orgRequestIdRef.current) return;
        setOrgResults(response.organisations);
      } catch {
        if (requestId !== orgRequestIdRef.current) return;
        setOrgResults([]);
        setOrgSearchError("Couldn't load organisations. Try again.");
      } finally {
        if (requestId === orgRequestIdRef.current) {
          setOrgSearchLoading(false);
        }
      }
    }, ORG_SEARCH_DEBOUNCE_MS);
  }, [orgSearch, isAccountContext, selectedOrganisation]);

  function handleRoleToggle(role: string) {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(existing => existing !== role) : [...prev, role],
    );
  }

  function handleOrgInputChange(value: string) {
    setOrgSearch(value);
    setIsOrgListOpen(true);
    if (selectedOrganisation && value !== selectedOrganisation.name) {
      setSelectedOrganisation(null);
    }
  }

  function handleOrgSelect(org: Organisation) {
    setSelectedOrganisation(org);
    setOrgSearch(org.name);
    setOrgResults([]);
    setIsOrgListOpen(false);
    setFieldErrors(prev => ({ ...prev, organisation: undefined }));
  }

  function validate(): boolean {
    const nextErrors: { name?: string; email?: string; organisation?: string } = {};
    if (name.trim().length < 1) nextErrors.name = "Name is required";
    if (email.trim().length < 1) {
      nextErrors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }
    if (isAccountContext && !selectedOrganisation) {
      nextErrors.organisation = "Select an organisation for this user";
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    setServerError(null);

    const payload: CreateUserPayload = {
      name: name.trim(),
      email: email.trim(),
      context,
      status: "verified",
      platformRoles: selectedRoles.length > 0 ? { staff: selectedRoles } : undefined,
      organisationId: isAccountContext ? selectedOrganisation?.id : undefined,
    };

    try {
      const created = await userManagement.createUser(payload);
      onSuccess(created);
    } catch (err) {
      const message = getFriendlyErrorMessage(err);
      setServerError(message);
      if (onError && err instanceof Error) onError(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  const showOrgDropdown =
    isAccountContext &&
    isOrgListOpen &&
    orgSearch.trim().length > 0 &&
    (!selectedOrganisation || orgSearch !== selectedOrganisation.name);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create user" size="medium">
      <form onSubmit={handleSubmit} className={createUserStyles.form}>
        {serverError && (
          <div className={createUserStyles.serverError} role="alert" aria-live="assertive">
            {serverError}
          </div>
        )}

        <Input
          label="Name"
          value={name}
          onChange={event => setName(event.target.value)}
          error={fieldErrors.name}
          disabled={isSubmitting}
          required
          fullWidth
          autoComplete="off"
        />

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={event => setEmail(event.target.value)}
          error={fieldErrors.email}
          disabled={isSubmitting}
          required
          fullWidth
          autoComplete="off"
        />

        {isAccountContext && (
          <div className={createUserStyles.orgPicker}>
            <Input
              label="Organisation"
              value={orgSearch}
              onChange={event => handleOrgInputChange(event.target.value)}
              onFocus={() => setIsOrgListOpen(true)}
              error={fieldErrors.organisation}
              disabled={isSubmitting}
              fullWidth
              autoComplete="off"
              placeholder="Search organisations by name..."
              role="combobox"
              aria-expanded={showOrgDropdown}
              aria-autocomplete="list"
              aria-controls="organisation-picker-listbox"
            />
            {showOrgDropdown && (
              <ul
                id="organisation-picker-listbox"
                role="listbox"
                className={createUserStyles.orgDropdown}
              >
                {orgSearchLoading && (
                  <li className={createUserStyles.orgDropdownStatus} aria-live="polite">
                    Searching...
                  </li>
                )}
                {!orgSearchLoading && orgSearchError && (
                  <li
                    className={createUserStyles.orgDropdownError}
                    role="alert"
                    aria-live="assertive"
                  >
                    {orgSearchError}
                  </li>
                )}
                {!orgSearchLoading && !orgSearchError && orgResults.length === 0 && (
                  <li className={createUserStyles.orgDropdownStatus} aria-live="polite">
                    No organisations found
                  </li>
                )}
                {!orgSearchLoading &&
                  !orgSearchError &&
                  orgResults.map(org => (
                    <li
                      key={org.id}
                      role="option"
                      aria-selected={selectedOrganisation?.id === org.id}
                      tabIndex={0}
                      className={createUserStyles.orgDropdownItem}
                      onClick={() => handleOrgSelect(org)}
                      onKeyDown={event => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleOrgSelect(org);
                        }
                      }}
                    >
                      {org.name}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}

        {!isAccountContext && (
          <div className={createUserStyles.rolesSection}>
            <div className={createUserStyles.rolesHeader}>
              <span className={createUserStyles.rolesLabel}>Platform roles</span>
              <span className={createUserStyles.rolesHelper}>
                Roles take effect when the user first logs in.
              </span>
            </div>
            <div className={styles.rolesGrid}>
              {STAFF_PLATFORM_ROLES.map(role => (
                <Tooltip
                  key={role.name}
                  title={role.name}
                  content={role.description || "(no description)"}
                  position="right"
                >
                  <label className={styles.checkboxRow}>
                    <Checkbox
                      checked={selectedRoles.includes(role.name)}
                      onChange={() => handleRoleToggle(role.name)}
                      label={role.name}
                      disabled={isSubmitting}
                    />
                  </label>
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <Button
            variant="outlined"
            color="default"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create user"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateUserModal;
